import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Header.scss";
import search from "../Assets/search.png";
// searimg was previously used as a profile icon; replaced by admin avatar
import API from "../Api/Api";
import endpoints from "../Api/Endpoints";

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardPage = location.pathname.toLowerCase() === "/dashboard";
  const currentPath = location.pathname.toLowerCase();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [listSearchOpen, setListSearchOpen] = useState(false);
  const [listSearchValue, setListSearchValue] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const listSearchInputRef = useRef(null);

  // Dashboard global search: name, city, pincode, area (radius / map search lives in Sidebar → Location search)
  useEffect(() => {
    if (!isDashboardPage) return;
    if (!query || query.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    const t = setTimeout(() => {
      setLoading(true);
      API.get(endpoints.ADMIN_SEARCH, { params: { q: query.trim() } })
        .then((res) => {
          const data = res?.data?.data ?? res?.data ?? {};
          setResults({
            doctors: data.doctors || [],
            hospitals: data.hospitals || [],
            patients: data.patients || [],
          });
          setOpen(true);
        })
        .catch(() => setResults({ doctors: [], hospitals: [], patients: [] }))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, isDashboardPage]);

  useEffect(() => {
    if (isDashboardPage) return;
    setQuery("");
    setResults(null);
    setOpen(false);
  }, [isDashboardPage]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    API.get(endpoints.ADMIN_ME)
      .then((res) => {
        const data = res?.data?.admin ?? res?.data;
        if (data) setAdmin(data);
      })
      .catch(() => setAdmin(null));
  }, []);

  const adminInitial = admin?.name ? admin.name.trim().charAt(0).toUpperCase() : "A";

  const moduleResults = [
    { id: "module-dashboard", label: "Dashboard", route: "/Dashboard" },
    { id: "module-location-search", label: "Location search", route: "/location-search" },
    { id: "module-patient-list", label: "Patient List", route: "/patient-list" },
    { id: "module-doctor-list", label: "Doctors", route: "/doctor-list" },
    { id: "module-hospital-list", label: "Hospitals", route: "/hospital-list" },
    { id: "module-bids", label: "Bids", route: "/Bid" },
    { id: "module-category-management", label: "Category Management", route: "/category-management" },
    { id: "module-gallery", label: "Gallery", route: "/gallery" },
    { id: "module-notifications", label: "Notifications", route: "/notifications" },
    { id: "module-review-approval", label: "Review Approval", route: "/review-approval" },
    { id: "module-prescriptions-reports", label: "Clinical files", route: "/prescriptions-reports" },
    { id: "module-patient-medical-submissions", label: "Patient Medical Submissions", route: "/patient-medical-submissions" },
    { id: "module-new-signups", label: "New Sign Ups", route: "/new-signups" },
    { id: "module-settings", label: "Settings", route: "/settings" },
  ].filter((item) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return false;
    return item.label.toLowerCase().includes(normalized);
  });

  const formatEntityMeta = (row) => {
    const parts = [];
    if (row.pincode) parts.push(`Pin ${row.pincode}`);
    if (row.locationLabel) parts.push(row.locationLabel);
    if (row.distanceKm != null && row.distanceKm !== "") {
      parts.push(`${row.distanceKm} km away`);
    }
    return parts.join(" · ");
  };

  const handleSelect = (type, idOrRoute) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    if (type === "route") {
      navigate(idOrRoute);
      return;
    }
    if (type === "patient") {
      const highlight = `patient-${idOrRoute}`;
      navigate(`/patient-list?highlight=${encodeURIComponent(highlight)}`);
    } else if (type === "doctor") {
      navigate(`/doctor-list/${encodeURIComponent(idOrRoute)}`);
    } else if (type === "hospital") {
      navigate(`/hospital-list/${encodeURIComponent(idOrRoute)}`);
    }
  };

  const hasEntityResults = results &&
    ((results.doctors && results.doctors.length) ||
     (results.hospitals && results.hospitals.length) ||
     (results.patients && results.patients.length));
  const hasAny = moduleResults.length > 0 || hasEntityResults;

  const showSearchDropdown = open && query.trim().length >= 2;

  const pageTitleByPath = {
    "/new-signups": "New Sign Ups",
    "/patient-list": "Patient List",
    "/provider-list": "Doctors / Hospitals",
    "/doctor-list": "Doctors",
    "/hospital-list": "Hospitals",
    "/bid": "Bids",
    "/active-bids": "Bids",
    "/notifications": "Notifications",
    "/review-approval": "Review Approval",
    "/category-management": "Category Management",
    "/gallery": "Gallery",
    "/prescriptions-reports": "Clinical files",
    "/bidresponsesoverview": "Bid Responses Overview",
    "/patient-medical-submissions": "Patient Medical Submissions",
    "/settings": "Settings",
    "/location-search": "Location search",
  };

  const nonDashboardTitle =
    currentPath.startsWith("/patient-list/") && currentPath !== "/patient-list"
      ? "Patient detail"
      : currentPath.startsWith("/doctor-list/") && currentPath !== "/doctor-list"
        ? "Doctor detail"
        : currentPath.startsWith("/hospital-list/") && currentPath !== "/hospital-list"
          ? "Hospital detail"
          : pageTitleByPath[currentPath] || "Admin";
  const isListPage =
    currentPath === "/patient-list" ||
    currentPath === "/provider-list" ||
    currentPath === "/doctor-list" ||
    currentPath === "/hospital-list";

  useEffect(() => {
    if (isListPage) return;
    setListSearchOpen(false);
    setListSearchValue("");
  }, [isListPage]);

  useEffect(() => {
    if (!isListPage || !listSearchOpen) return;
    try {
      if (window.matchMedia("(max-width: 1100px)").matches) return;
    } catch (_) {}
    listSearchInputRef.current?.focus();
  }, [isListPage, listSearchOpen]);

  const handleToggleListSearch = () => {
    setListSearchOpen((prev) => {
      const next = !prev;
      window.dispatchEvent(
        new CustomEvent("admin:new-signups-toggle-search", {
          detail: { open: next },
        })
      );
      if (!next) {
        setListSearchValue("");
        window.dispatchEvent(
          new CustomEvent("admin:new-signups-search-change", {
            detail: "",
          })
        );
      }
      return next;
    });
  };

  const handleHeaderListSearchChange = (e) => {
    const value = e.target.value;
    setListSearchValue(value);
    window.dispatchEvent(
      new CustomEvent("admin:new-signups-search-change", {
        detail: value,
      })
    );
  };

  return (
    <div className={`admin_header${!isDashboardPage ? " admin_header--no-search" : ""}`}>
      {typeof onMenuClick === "function" && (
        <button
          type="button"
          className="header_menu_btn"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <span className="header_menu_btn__bars" aria-hidden>
            <span />
            <span />
            <span />
          </span>
        </button>
      )}
      {!isDashboardPage && (
        <div className="page_title_group">
          <h2 className="page_title">{nonDashboardTitle}</h2>
        </div>
      )}
      {isDashboardPage && (
        <div className="search_box" ref={dropdownRef}>
          <div className="search_input_row">
            <img src={search} className="search_icon" alt="" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search name, city, pincode, area…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.trim().length >= 2 && setOpen(true)}
            />
          </div>
          {showSearchDropdown && (
            <div className="search_dropdown">
              {loading ? (
                <div className="search_dropdown_item search_dropdown_loading">Searching…</div>
              ) : !hasAny ? (
                <div className="search_dropdown_item search_dropdown_empty">No results</div>
              ) : (
                <>
                  {moduleResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="search_dropdown_item"
                      onClick={() => handleSelect("route", item.route)}
                    >
                      <span className="search_dropdown_label">Module</span>
                      <span className="search_dropdown_name">{item.label}</span>
                    </button>
                  ))}
                  {(results?.doctors || []).map((d) => {
                    const meta = formatEntityMeta(d);
                    return (
                      <button
                        key={`doctor-${d.id}`}
                        type="button"
                        className="search_dropdown_item"
                        onClick={() => handleSelect("doctor", d.id)}
                      >
                        <span className="search_dropdown_label">Doctor</span>
                        <span className="search_dropdown_name">{d.name}</span>
                        {meta ? (
                          <span className="search_dropdown_meta">{meta}</span>
                        ) : null}
                      </button>
                    );
                  })}
                  {(results?.hospitals || []).map((h) => {
                    const meta = formatEntityMeta(h);
                    return (
                      <button
                        key={`hospital-${h.id}`}
                        type="button"
                        className="search_dropdown_item"
                        onClick={() => handleSelect("hospital", h.id)}
                      >
                        <span className="search_dropdown_label">Hospital</span>
                        <span className="search_dropdown_name">{h.name}</span>
                        {meta ? (
                          <span className="search_dropdown_meta">{meta}</span>
                        ) : null}
                      </button>
                    );
                  })}
                  {(results?.patients || []).map((p) => {
                    const meta = formatEntityMeta(p);
                    return (
                      <button
                        key={`patient-${p.id}`}
                        type="button"
                        className="search_dropdown_item"
                        onClick={() => handleSelect("patient", p.id)}
                      >
                        <span className="search_dropdown_label">Patient</span>
                        <span className="search_dropdown_name">{p.name}</span>
                        {meta ? (
                          <span className="search_dropdown_meta">{meta}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="right_area" ref={profileRef}>
        {isListPage && (
          <div className="header_list_actions">
            <button
              type="button"
              className={`header_list_icon_btn${listSearchOpen ? " is-active" : ""}`}
              onClick={handleToggleListSearch}
              aria-label={listSearchOpen ? "Close search" : "Open search"}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M11 4a7 7 0 1 0 4.44 12.41l4.07 4.08a1 1 0 0 0 1.42-1.42l-4.08-4.07A7 7 0 0 0 11 4Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" />
              </svg>
            </button>
            <div className={`header_list_search_box${listSearchOpen ? " open" : ""}`}>
              <input
                ref={listSearchInputRef}
                type="text"
                value={listSearchValue}
                onChange={handleHeaderListSearchChange}
                placeholder="Search by name or mobile"
              />
            </div>
            <button
              type="button"
              className="header_list_icon_btn"
              aria-label="Open filters"
              onClick={() => {
                window.dispatchEvent(new Event("admin:new-signups-open-filter"));
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6L14 14.5V19a1 1 0 0 1-.55.9l-3 1.5A1 1 0 0 1 9 20.5v-6L3.2 5.6A1 1 0 0 1 3 5Z" />
              </svg>
            </button>
          </div>
        )}
        <button
          type="button"
          className="admin_avatar_btn admin_avatar_btn--static"
          title="Admin"
          aria-label="Admin"
        >
          <span className="admin_avatar" aria-hidden="true">{adminInitial}</span>
        </button>
      </div>
    </div>
  );
}
